import { useEffect, useMemo, useState } from 'react';
import { AccessPermission, AccessRole, saveRolePermissions } from '../../../api/accessApi';
import { useAsync } from './shared';

interface RolesTabProps {
  roles: AccessRole[];
  permissions: AccessPermission[];
  /** Permission names the signed-in user may tick or untick. */
  grantable: string[];
  onSaved: () => void;
}

const byOrder = (a: AccessPermission, b: AccessPermission) => a.order - b.order || a.label.localeCompare(b.label);

export default function RolesTab({ roles, permissions, grantable, onSaved }: RolesTabProps) {
  const [draft, setDraft] = useState<Record<number, Set<string>>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const { error, busy, run } = useAsync();

  useEffect(() => {
    setDraft(Object.fromEntries(roles.map((r) => [r.id, new Set(r.permissions)])));
  }, [roles]);

  const canGrant = useMemo(() => new Set(grantable), [grantable]);
  const system = useMemo(() => permissions.filter((p) => p.kind === 'system').sort(byOrder), [permissions]);
  const sections = useMemo(() => permissions.filter((p) => p.kind === 'section').sort(byOrder), [permissions]);
  const toolsBySection = useMemo(() => {
    const groups: Record<string, AccessPermission[]> = {};
    permissions
      .filter((p) => p.kind === 'tool' && p.section)
      .forEach((p) => (groups[p.section as string] ||= []).push(p));
    Object.values(groups).forEach((list) => list.sort(byOrder));
    return groups;
  }, [permissions]);

  const toggle = (roleId: number, name: string) =>
    setDraft((prev) => {
      const next = new Set(prev[roleId]);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return { ...prev, [roleId]: next };
    });

  const dirty = (role: AccessRole) => {
    const current = draft[role.id];
    return !!current && (current.size !== role.permissions.length || role.permissions.some((p) => !current.has(p)));
  };

  const save = async (role: AccessRole) => {
    const done = await run(() => saveRolePermissions(role.id, Array.from(draft[role.id])));
    if (done) onSaved();
  };

  const editableRoles = roles.filter((r) => r.editable);
  const cellDisabled = (role: AccessRole, name: string) => !role.editable || !canGrant.has(name) || busy;

  const checkboxCell = (role: AccessRole, p: AccessPermission, inheritedFrom?: string) => {
    const inherited = !!inheritedFrom && !!draft[role.id]?.has(inheritedFrom);
    return (
      <td key={role.id} className="acc-check">
        <input
          type="checkbox"
          aria-label={`${role.name}: ${p.label}`}
          title={inherited ? 'Included through the whole section' : undefined}
          disabled={cellDisabled(role, p.name) || inherited}
          checked={inherited || (draft[role.id]?.has(p.name) ?? false)}
          onChange={() => toggle(role.id, p.name)}
        />
      </td>
    );
  };

  return (
    <section className="panel">
      <h2>Roles &amp; permissions</h2>
      <p className="acc-hint">
        {editableRoles.length > 0
          ? 'Tick “Whole section” to give a role every tool in it, or expand a section to pick individual tools. ' +
            'You can only change roles below your own, and only access you hold yourself.'
          : 'Read only. You do not have permission to change what roles can access.'}
      </p>
      {error && <p className="acc-error">{error}</p>}
      <div className="acc-scroll">
        <table className="acc-table acc-grid">
          <thead>
            <tr>
              <th>Access</th>
              {roles.map((r) => (
                <th key={r.id}>
                  {r.name}
                  <span>{r.editable ? `level ${r.level}` : `level ${r.level} · view only`}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {system.length > 0 && (
              <tr className="acc-group">
                <td colSpan={roles.length + 1}>Administration</td>
              </tr>
            )}
            {system.map((p) => (
              <tr key={p.id}>
                <td>
                  <b>{p.name}</b>
                  <span>{p.description}</span>
                </td>
                {roles.map((r) => checkboxCell(r, p))}
              </tr>
            ))}

            {sections.map((section) => {
              const tools = toolsBySection[section.section as string] ?? [];
              const expanded = !!open[section.section as string];
              return [
                <tr key={section.id} className="acc-group">
                  <td>
                    <button
                      type="button"
                      className="acc-expand"
                      aria-expanded={expanded}
                      onClick={() => setOpen((cur) => ({ ...cur, [section.section as string]: !expanded }))}
                    >
                      {expanded ? '▾' : '▸'} {section.label}
                    </button>
                    <span>
                      Whole section · {tools.length} tool{tools.length === 1 ? '' : 's'}
                    </span>
                  </td>
                  {roles.map((r) => {
                    const granted = draft[r.id];
                    const some = tools.some((t) => granted?.has(t.name));
                    const wholeSection = granted?.has(section.name) ?? false;
                    return (
                      <td key={r.id} className="acc-check">
                        <input
                          type="checkbox"
                          aria-label={`${r.name}: whole ${section.label} section`}
                          disabled={cellDisabled(r, section.name)}
                          checked={wholeSection}
                          ref={(el) => {
                            if (el) el.indeterminate = !wholeSection && some;
                          }}
                          onChange={() => toggle(r.id, section.name)}
                        />
                      </td>
                    );
                  })}
                </tr>,
                ...(expanded
                  ? tools.map((tool) => (
                      <tr key={tool.id}>
                        <td className="acc-indent">{tool.label}</td>
                        {roles.map((r) => checkboxCell(r, tool, section.name))}
                      </tr>
                    ))
                  : []),
              ];
            })}

            {editableRoles.length > 0 && (
              <tr>
                <td />
                {roles.map((r) => (
                  <td key={r.id} className="acc-check">
                    {r.editable && (
                      <button className="btn primary" disabled={!dirty(r) || busy} onClick={() => save(r)}>
                        Save
                      </button>
                    )}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
